/**
 * creatures.js - Door Knock's vocabulary.
 *
 * CONTENT ONLY. Nothing here knows what a door is.
 *
 * Deliberately no overlap with Bubble Pop's catalogue. Section 9.3 verifies a
 * skill by seeing the same LEVEL go Green in two games; if both games used the
 * same words, Green in the second would show the child had learned the words,
 * which is the thing generalization is supposed to rule out.
 *
 * Same rules as any object catalogue: an `accept` list per word because the
 * recogniser returns dictionary words and a target is a SET of strings; no
 * minimal pairs inside one pool; a clue that never contains its own answer.
 */

/**
 * TWO WORDS THAT ARE NOT HERE ANY MORE
 *
 * `fox` and `crab` were, and a real session stored `expected "fox", heard
 * "fuck"` and `expected "crab", heard "crap"`. The recogniser returns the
 * nearest dictionary word, and a child learning to talk hands it exactly the
 * imprecise articulation that makes the nearest word the wrong one.
 *
 * A word is unusable here if its likely mis-recognition is something no parent
 * may see attributed to their child. `engine/language.js` screens for it and a
 * conformance check refuses the ones it finds - but the screen is a screen, not
 * a proof, so think about it when you choose a word.
 */
export const CREATURES = {
  frog:  { id: 'frog',  word: 'frog',  accept: ['frog', 'frogs'],   art: '🐸', clue: 'It is green and it hops.',              tint: '#8fd67a', firstLetter: 'Fr' },
  owl:   { id: 'owl',   word: 'owl',   accept: ['owl', 'owls'],     art: '🦉', clue: 'It stays awake all night and hoots.',   tint: '#c9a978', firstLetter: 'O'  },
  wolf:  { id: 'wolf',  word: 'wolf',  accept: ['wolf', 'wolves'],  art: '🐺', clue: 'It howls at the moon.',                 tint: '#ff9f5c', firstLetter: 'W'  },
  mouse: { id: 'mouse', word: 'mouse', accept: ['mouse'],           art: '🐭', clue: 'It is very small and grey.',            tint: '#c3c8d4', firstLetter: 'M'  },
  snail: { id: 'snail', word: 'snail', accept: ['snail', 'snails'], art: '🐌', clue: 'It carries its house and goes slowly.', tint: '#d8c48a', firstLetter: 'Sn' },
  lamb:  { id: 'lamb',  word: 'lamb',  accept: ['lamb', 'lambs'],   art: '🐑', clue: 'It is a baby sheep and it says baa.',   tint: '#ff8a80', firstLetter: 'L'  },
  hen:   { id: 'hen',   word: 'hen',   accept: ['hen', 'hens'],     art: '🐔', clue: 'It lays eggs and says cluck.',          tint: '#ffd18a', firstLetter: 'H'  },
  goat:  { id: 'goat',  word: 'goat',  accept: ['goat', 'goats'],   art: '🐐', clue: 'It has horns and eats everything.',     tint: '#bfae94', firstLetter: 'G'  },
};

/**
 * Phase 3 carriers.
 *
 * The set is the FRAMEWORK's ({want, put, get, more}, section 6.4), not this
 * game's - which is why Door Knock can offer "get" where Bubble Pop offers
 * "put" and the same box still means the same thing. Each does something
 * different, or the child correctly learns the verb is decoration.
 */
export const CARRIERS = {
  want: { id: 'want', word: 'want', accept: ['want', 'wants'], effect: 'come',   label: 'want', art: '🙌' },
  get:  { id: 'get',  word: 'get',  accept: ['get', 'gets'],   effect: 'fetch',  label: 'get',  art: '🧺' },
  more: { id: 'more', word: 'more', accept: ['more'],          effect: 'again',  label: 'more', art: '➕' },
};

/**
 * Phase 4's grammar pieces.
 *
 * `verbs` are the -ing forms component A looks for; `places` are the
 * prepositions component B looks for, drawn from the closed set section 6.5
 * names ({in, on, under, next to}) because those are the ones the recogniser
 * transcribes reliably.
 */
export const VERBS = {
  putting: { id: 'putting', word: 'putting', accept: ['putting', 'put in', 'puttin'] },
  going:   { id: 'going',   word: 'going',   accept: ['going', 'goin'] },
};

export const PLACES = {
  in:    { id: 'in',    word: 'in',    accept: ['in'],       art: '🧺', label: 'in the basket' },
  on:    { id: 'on',    word: 'on',    accept: ['on'],       art: '🏠', label: 'on the roof' },
  under: { id: 'under', word: 'under', accept: ['under'],    art: '🌳', label: 'under the tree' },
};

export const CREATURE_IDS = Object.keys(CREATURES);

export function getCreature(id) { return CREATURES[id] || null; }
export function getCarrier(id) { return CARRIERS[id] || null; }
export function getPlace(id) { return PLACES[id] || null; }
export function getVerb(id) { return VERBS[id] || null; }

/**
 * The phrase Pip models and the prompt displays.
 * @param {{creatureId:string, carrierId?:string, verbId?:string, placeId?:string}} target
 */
export function canonicalPhrase(target) {
  const creature = getCreature(target.creatureId);
  if (!creature) return '';
  if (target.verbId) {
    const verb = getVerb(target.verbId);
    const place = getPlace(target.placeId);
    return [verb && verb.word, creature.word, place && place.word].filter(Boolean).join(' ');
  }
  if (target.carrierId) {
    const carrier = getCarrier(target.carrierId);
    return carrier ? `${carrier.word} ${creature.word}` : creature.word;
  }
  return creature.word;
}

/** Every string that counts as having produced this target. */
export function acceptedPhrases(target) {
  const creature = getCreature(target.creatureId);
  if (!creature) return [];

  if (target.verbId) {
    const verb = getVerb(target.verbId);
    const place = getPlace(target.placeId);
    const out = [];
    for (const v of verb.accept) {
      for (const c of creature.accept) {
        for (const p of (place ? place.accept : [''])) {
          out.push([v, c, p].filter(Boolean).join(' '));
        }
      }
    }
    return out;
  }

  if (target.carrierId) {
    const carrier = getCarrier(target.carrierId);
    const out = [];
    for (const c of carrier.accept) for (const n of creature.accept) out.push(`${c} ${n}`);
    return out;
  }

  return creature.accept.slice();
}

export default {
  CREATURES, CARRIERS, VERBS, PLACES, CREATURE_IDS,
  getCreature, getCarrier, getPlace, getVerb, canonicalPhrase, acceptedPhrases,
};
